/**
 * Measurement hint computation for catalog field selections.
 *
 * Computes oversize % and remaining length warnings by comparing
 * selected graft values against patient anatomic measurements.
 *
 * Pure functions — no React or service dependencies.
 */

import type { IntroductionSide, MeasurementLookup } from './assembly-types';
import type { ICatalogField, Side } from './types';

// ============================================================================
// Types
// ============================================================================

export type HintInfo =
    | { type: 'oversize'; percent: number }
    | { type: 'remaining_length'; tooLong: boolean; delta: number }
    | null;

// ============================================================================
// Computation
// ============================================================================

/**
 * Get the relevant measurement IDs for a field based on side.
 * Symmetric measures apply to both sides; L/R measures are side-specific.
 *
 * When `side` is 'none' (e.g., bifurcated_up parts), `introductionSide` is
 * used to resolve L/R measures — the introduction side determines which
 * anatomical side the measurement refers to.
 */
export function getMeasureIds(
    field: ICatalogField,
    side: Side,
    introductionSide?: IntroductionSide
): string[] {
    if (field.measures && field.measures.length > 0) {
        return field.measures;
    }
    // Use introduction side as fallback when part side is 'none'
    const effectiveSide = side !== 'none' ? side : introductionSide;
    if (effectiveSide === 'left' && field.measuresLeft) {
        return field.measuresLeft;
    }
    if (effectiveSide === 'right' && field.measuresRight) {
        return field.measuresRight;
    }
    return field.measuresLeft ?? field.measuresRight ?? [];
}

/**
 * Get the average measurement value for a set of measurement IDs.
 */
function getAverageMeasurement(measureIds: string[], lookup: MeasurementLookup): number | null {
    const values = measureIds
        .map((id) => lookup[id]?.value)
        .filter((v): v is number => v != null && !isNaN(v));

    if (values.length === 0) return null;
    return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/**
 * Compute a hint for a specific field value given measurement data.
 *
 * @param field - The catalog field definition
 * @param selectedValue - The currently selected numeric value (null if none)
 * @param measurementLookup - Patient measurement values
 * @param side - Which side this part instance represents
 * @returns Hint info or null if not applicable
 */
export function computeFieldHint(
    field: ICatalogField,
    selectedValue: number | null,
    measurementLookup: MeasurementLookup,
    side: Side,
    introductionSide?: IntroductionSide
): HintInfo {
    if (!field.info || selectedValue == null) return null;

    const measureIds = getMeasureIds(field, side, introductionSide);
    if (measureIds.length === 0) return null;

    const patientValue = getAverageMeasurement(measureIds, measurementLookup);
    if (patientValue == null || patientValue === 0) return null;

    if (field.info === 'oversize') {
        const percent = Math.round(100 * (selectedValue / patientValue - 1));
        return { type: 'oversize', percent };
    }

    if (field.info === 'remaining_length') {
        const delta = selectedValue - patientValue;
        return { type: 'remaining_length', tooLong: delta > 0, delta: Math.round(delta) };
    }

    return null;
}
